import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import DropDown from '../DropDown/DropDown';
import { launchCreator } from '../../utils/launchCreator';
import { getParent, getParentPath } from '../../utils/parent';
import { getPermissions } from '../../utils/getPermissions';
import ToolTip from '../ToolTipIcon/ToolTip';
import urlHelper from '../../utils/urlHelper';

export function ContentSelectorWithoutRedux(props) {
  const [showUploadDropDown, setShowUploadDropDown] = useState(false);
  const showUploadDropDownTogglerRef = useRef(null);
  const { creatorProps, auth, setShowUploadDialog, setShowFolderDialog } = props;
  const location = useLocation();
  const selectorBg = type =>
    urlHelper.getUrlParam(location.search, 'type', []).includes(type) ? 'bg-sunrise' : 'bg-white';

  /**
   * Toggles type filter on current url
   * @param {string} type
   * @return {string} new url
   */
  const toggleTypeFilter = type => {
    const currentUrl = location.search;
    const types = urlHelper.getUrlParam(currentUrl, 'type', []);

    if (types.includes(type)) {
      // an empty query string would make NavLink resolve to the root /, so fall back to '?'
      return urlHelper.removeFromUrlParam(currentUrl, 'type', type) || '?';
    }

    return urlHelper.addToUrlParam(currentUrl, 'type', type);
  };

  const clientAndUserPermissions = getPermissions(auth);
  const parent = getParentPath(props);
  const query = parent === '/' ? '' : `?parent=${encodeURIComponent(parent)}`;

  return (
    <div data-test="content-selector-container" className="mt-8">
      <div className="flex  md:space-x-4 md:ml-0 justify-between md:justify-start">
        <ToolTip toolText="Images">
          <div className="flex items-center justify-center flex-col">
            <NavLink
              data-test="content-selector-images"
              to={toggleTypeFilter('image')}
              className={`duration-500 hover:scale-105 w-[50px] cursor-pointer h-[50px] flex justify-center items-center ${selectorBg('image')} rounded-lg`}
            >
              <img
                src="/assets/images/icons/gallery_icon.svg"
                alt="load images"
                className="w-[30px] h-[30px]"
                draggable={false}
              />
            </NavLink>
            <p className="text-xs mt-1 text-[#3B6C81]">Images</p>
          </div>
        </ToolTip>

        <ToolTip toolText="Videos">
          <div className="flex items-center justify-center flex-col">
            <NavLink
              data-test="content-selector-video"
              to={toggleTypeFilter('video')}
              className={`duration-500 hover:scale-105 w-[50px] cursor-pointer h-[50px] flex justify-center items-center ${selectorBg('video')} rounded-lg`}
            >
              <img
                src="/assets/images/icons/video_icon.svg"
                alt="video icon"
                className="w-[30px] h-[30px]"
                draggable={false}
              />
            </NavLink>
            <p className="text-xs mt-1 text-[#3B6C81]">Videos</p>
          </div>
        </ToolTip>
        <ToolTip toolText="Playlists">
          <div className="flex items-center justify-center flex-col">
            <NavLink
              data-test="content-selector-playlists"
              to={toggleTypeFilter('playlist')}
              className={`duration-500 hover:scale-105 w-[50px] cursor-pointer h-[50px] flex justify-center items-center ${selectorBg('playlist')} rounded-lg`}
            >
              <img
                src="/assets/images/icons/table_icon.svg"
                alt="table icon"
                className="w-[30px] h-[30px]"
                draggable={false}
              />
            </NavLink>
            <p className="text-xs mt-1 text-[#3B6C81]">Playlists</p>
          </div>
        </ToolTip>

        <ToolTip toolText="Folders">
          <div className="flex items-center justify-center flex-col">
            <NavLink
              data-test="content-selector-folders"
              to={toggleTypeFilter('folder')}
              className={`duration-500 hover:scale-105 w-[50px] cursor-pointer h-[50px] flex justify-center items-center ${selectorBg('folder')} rounded-lg`}
            >
              <img
                src="/assets/images/icons/folder_icon.svg"
                alt="folder icon"
                className="w-[30px] h-[30px]"
                draggable={false}
              />
            </NavLink>
            <p className="text-xs mt-1 text-[#3B6C81]">Folders</p>
          </div>
        </ToolTip>

        <ToolTip toolText="Signs">
          <div className="flex items-center justify-center flex-col">
            <NavLink
              data-test="content-selector-signs"
              to={toggleTypeFilter('sign')}
              className={`duration-500 hover:scale-105 w-[50px] cursor-pointer h-[50px] flex justify-center items-center ${selectorBg('sign')} rounded-lg`}
            >
              <img
                src="/assets/images/icons/desktop_icon.svg"
                alt="desktop icon"
                className="w-[30px] h-[30px]"
                draggable={false}
              />
            </NavLink>
            <p className="text-xs mt-1 text-[#3B6C81]">Signs</p>
          </div>
        </ToolTip>

        <ToolTip toolText="Sequence">
          <div className="flex items-center justify-center flex-col">
            <NavLink
              data-test="content-selector-sequence"
              to={toggleTypeFilter('sequence')}
              className={`duration-500 hover:scale-105 w-[50px] cursor-pointer h-[50px] flex justify-center items-center ${selectorBg('sequence')} rounded-lg`}
            >
              <img
                src="/assets/images/icons/sequence.svg"
                alt="desktop icon"
                className="w-[30px] h-[30px]"
                draggable={false}
              />
            </NavLink>
            <p className="text-xs mt-1 text-[#3B6C81]">Sequence</p>
          </div>
        </ToolTip>
        <ToolTip toolText="Schedule">
          <div className="flex items-center justify-center flex-col ">
            <NavLink
              data-test="content-selector-schedule"
              to={toggleTypeFilter('schedule')}
              className={`duration-500 hover:scale-105 w-[50px] cursor-pointer h-[50px] flex justify-center items-center ${selectorBg('schedule')} rounded-lg`}
            >
              <span className="text-dark-night material-icons w-[30px] h-[30px]">restore</span>
            </NavLink>
            <p className="text-xs mt-1 text-[#3B6C81]">Schedule</p>
          </div>
        </ToolTip>
        <ToolTip toolText="Campaign">
          <div className="flex items-center justify-center flex-col">
            <NavLink
              data-test="content-selector-campaign"
              to={toggleTypeFilter('campaign')}
              className={`duration-500 hover:scale-105 w-[50px] cursor-pointer h-[50px] flex justify-center items-center ${selectorBg('campaign')} rounded-lg`}
            >
              <img
                src="/assets/images/icons/speakerphone.svg"
                alt="desktop icon"
                className="w-[30px] h-[30px]"
                draggable={false}
              />
            </NavLink>
            <p className="text-xs mt-1 text-[#3B6C81]">Campaign</p>
          </div>
        </ToolTip>

        <ToolTip toolText="Upload">
          <div className="flex items-center justify-center flex-col">
            <button
              type="button"
              data-test="content-selector-upload-file"
              className="duration-500 hover:scale-105 w-[50px] text-[#0A364B] cursor-pointer h-[50px] flex justify-center items-center bg-white rounded-lg"
              onClick={() => {
                setShowUploadDropDown(false);
                setShowUploadDialog(true);
              }}
            >
              <img
                src="/assets/images/icons/file_upload.svg"
                alt="desktop icon"
                className="w-[30px] h-[30px]"
                draggable={false}
              />
            </button>
            <p className="text-xs mt-1 text-[#3B6C81]">Upload</p>
          </div>
        </ToolTip>

        <div className="relative h-[50px]">
          <button
            data-test="content-selector-add-button"
            ref={showUploadDropDownTogglerRef}
            type="button"
            className="w-[50px] relative cursor-pointer h-[50px] flex justify-center items-center bg-white rounded-lg"
            onClick={() => {
              setShowUploadDropDown(!showUploadDropDown);
            }}
          >
            <img
              src="/assets/images/icons/upload_icon.svg"
              alt="upload icon"
              className="w-[50px] h-[50px] cursor-pointer"
              draggable={false}
            />
          </button>
          <p className="text-xs mt-1 text-[#3B6C81] text-center w-[50px]">New</p>

          <DropDown
            show={showUploadDropDown}
            hide={() => setShowUploadDropDown(false)}
            togglerRef={showUploadDropDownTogglerRef}
          >
            <div className="relative max-h-[500px] overflow-y-auto md:translate-x-0 -translate-x-1/2">
              <div className="border-t border-gray-200 divide-y divide-gray-200 border-b-200">
                {clientAndUserPermissions.atLeastTierPro && (
                  <>
                    <button
                      data-test="content-selector-new-folder-button"
                      type="button"
                      className="appearance-none poppins-regular text-dark-night w-[200px] h-[40px] rounded-[15px] bg-slate-50 hover:bg-gray-100"
                      onClick={() => setShowFolderDialog(true)}
                    >
                      Folder
                    </button>

                    <button
                      data-test="content-selector-new-sign"
                      type="button"
                      className="appearance-none poppins-regular text-dark-night w-[200px] h-[40px] rounded-[15px] bg-slate-50 hover:bg-gray-100"
                      onClick={() => {
                        launchCreator(0, getParent(creatorProps).id);
                        setShowUploadDropDown(false);
                      }}
                    >
                      Sign
                    </button>
                    <Link
                      data-test="content-selector-new-campaign"
                      to={`/campaigns/new${query}`}
                      className="poppins-regular text-dark-night flex items-center justify-center   appearance-none w-[200px] h-[40px] rounded-[15px] bg-slate-50 hover:bg-gray-100"
                    >
                      Campaign
                    </Link>
                  </>
                )}

                <Link
                  data-test="content-selector-new-sequence"
                  to={`/sequences/new${query}`}
                  className=" poppins-regular text-dark-night flex items-center justify-center w-[200px] h-[40px] rounded-[15px] bg-slate-50 hover:bg-gray-100"
                >
                  Sequence
                </Link>
                <Link
                  data-test="content-selector-new-schedule"
                  to={`/schedules/new${query}`}
                  className="poppins-regular text-dark-night flex items-center justify-center  appearance-none w-[200px] h-[40px] rounded-[15px] bg-slate-50 hover:bg-gray-100"
                >
                  Schedule
                </Link>
                <Link
                  data-test="content-selector-new-playlist"
                  to={`/playlists/new${query}`}
                  className="poppins-regular text-dark-night flex items-center justify-center  appearance-none w-[200px] h-[40px] rounded-[15px] bg-slate-50 hover:bg-gray-100"
                >
                  Playlist
                </Link>
                <button
                  data-test="icon-UploadAssets"
                  type="button"
                  className="appearance-none w-[200px] h-[40px] rounded-[15px] bg-slate-50 hover:bg-gray-100 poppins-regular text-dark-night"
                  onClick={() => {
                    setShowUploadDropDown(false);
                    setShowUploadDialog(true);
                  }}
                >
                  Upload Asset
                </button>
              </div>
            </div>
          </DropDown>
        </div>
      </div>
    </div>
  );
}

ContentSelectorWithoutRedux.propTypes = {
  creatorProps: PropTypes.object.isRequired,
  auth: PropTypes.object.isRequired,
  setShowUploadDialog: PropTypes.func.isRequired,
  setShowFolderDialog: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  auth: state.auth,
});

export default connect(mapStateToProps)(ContentSelectorWithoutRedux);
